import axios from 'axios';
import type {
  AxiosInstance,
  AxiosResponse,
  AxiosRequestConfig,
  RawAxiosRequestHeaders,
} from 'axios';

import { ConsoleLogger } from '../logging';
import type { ILogger } from '../logging';

import type { Interceptor } from './interceptor';
import type { Token } from './token';


export type ClientOptions = {
  /**
   * The client name
   */
  readonly name?: string;

  /**
   * The authorization token to use
   */
  readonly token?: Token;

  /**
   *: ILogger instance to use
   */
  readonly logger?: ILogger;

  /**
   * The baseUrl to prefix all client requests with
   */
  readonly baseUrl?: string;

  /**
   * Default request timeout (ms)
   */
  readonly timeout?: number;

  /**
   * Default headers
   */
  readonly headers?: RawAxiosRequestHeaders;

  /**
   * Default interceptors to register
   */
  readonly interceptors?: Array<Interceptor>;
};

export type RequestConfig<D = any> = AxiosRequestConfig<D> & {
  /**
   * If provided, this token will be used instead of
   * the default token provided in the `ClientOptions`
   */
  token?: Token;
};

type InterceptorRegistry = {
  readonly requestId?: number;
  readonly responseId?: number;
  readonly interceptor: Interceptor;
};

export class Client {
  token?: Token;
  readonly name: string;

  protected options: ClientOptions;
  protected log: ILogger;
  protected http: AxiosInstance;
  protected seq: number = 0;
  protected interceptors: Map<number, InterceptorRegistry>;

  constructor(options: ClientOptions = {}) {
    this.options = options;
    this.name = options.name || 'http';
    this.token = options.token;
    this.log = options.logger || new ConsoleLogger(this.name);
    this.interceptors = new Map<number, InterceptorRegistry>();
    this.http = axios.create({
      baseURL: options.baseUrl,
      timeout: options.timeout,
      headers: options.headers,
    });

    for (const interceptor of options.interceptors || []) {
      this.use(interceptor);
    }
  }

  async get<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    config?: RequestConfig<D>,
  ): Promise<R> {
    return this.http.get<T, R, D>(url, await this.withConfig(config));
  }

  async post<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    data?: D,
    config?: RequestConfig<D>,
  ): Promise<R> {
    return this.http.post<T, R, D>(url, data, await this.withConfig(config));
  }

  async put<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    data?: D,
    config?: RequestConfig<D>,
  ): Promise<R> {
    return this.http.put<T, R, D>(url, data, await this.withConfig(config));
  }

  async patch<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    data?: D,
    config?: RequestConfig<D>,
  ): Promise<R> {
    return this.http.patch<T, R, D>(url, data, await this.withConfig(config));
  }

  async delete<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    config?: RequestConfig<D>,
  ): Promise<R> {
    return this.http.delete<T, R, D>(url, await this.withConfig(config));
  }

  async request<T = any, R = AxiosResponse<T>, D = any>(
    config: RequestConfig<D>,
  ): Promise<R> {
    return this.http.request<T, R, D>(await this.withConfig(config));
  }

  /**
   * Register an interceptor to use
   * as middleware for the request/response/error
   */
  use(interceptor: Interceptor): number {
    const id = ++this.seq;
    let requestId: number | undefined = undefined;
    let responseId: number | undefined = undefined;

    if (interceptor.request) {
      requestId = this.http.interceptors.request.use(
        /* istanbul ignore next */
        (config) => {
          return interceptor.request!({ config, log: this.log });
        },
        /* istanbul ignore next */
        (error: any) => {
          if (!interceptor.error) return error;
          return interceptor.error({ error, log: this.log });
        },
      );
    }

    if (interceptor.response) {
      responseId = this.http.interceptors.response.use(
        /* istanbul ignore next */
        (res) => {
          return interceptor.response!({ res, log: this.log });
        },
        /* istanbul ignore next */
        (error: any) => {
          if (!interceptor.error) return error;
          return interceptor.error({ error, log: this.log });
        },
      );
    }

    this.interceptors.set(id, {
      requestId,
      responseId,
      interceptor,
    });

    return id;
  }

  /**
   * Eject an interceptor
   */
  eject(id: number): void {
    const registry = this.interceptors.get(id);

    if (!registry) return;

    if (registry.requestId) {
      this.http.interceptors.request.eject(registry.requestId);
    }

    if (registry.responseId) {
      this.http.interceptors.response.eject(registry.responseId);
    }

    this.interceptors.delete(id);
  }

  /**
   * Clear (Eject) all interceptors
   */
  clear(): void {
    for (const id of this.interceptors.keys()) {
      this.eject(id);
    }
  }

  /**
   * Create a copy of the client
   */
  clone(options?: ClientOptions): Client {
    const findUA = (h?: RawAxiosRequestHeaders): string | undefined => {
      if (!h) return undefined;
      const key = Object.keys(h).find((k) => k.toLowerCase() === 'user-agent');
      return key ? String(h[key]) : undefined;
    };

    const parentUA = findUA(this.options.headers);
    const childUA = findUA(options?.headers);
    const mergedUA =
      parentUA && childUA ? `${childUA} ${parentUA}` : childUA || parentUA;

    const headers = {
      ...this.options.headers,
      ...options?.headers,
    };

    if (mergedUA) {
      for (const key of Object.keys(headers)) {
        if (key.toLowerCase() === 'user-agent') {
          delete headers[key];
        }
      }

      headers['User-Agent'] = mergedUA;
    }

    return new Client({
      ...this.options,
      ...options,
      headers,
      interceptors: [
        ...Array.from(this.interceptors.values()).map((i) => i.interceptor),
      ],
    });
  }

  protected async withConfig(
    config: RequestConfig = {},
  ): Promise<RequestConfig> {
    let token = config.token || this.token;

    if (config.token) {
      delete config.token;
    }

    if (this.options.headers) {
      if (!config.headers) {
        config.headers = {};
      }

      for (const key in this.options.headers) {
        config.headers[key] = this.options.headers[key];
      }
    }

    if (token) {
      if (!config.headers) {
        config.headers = {};
      }

      if (typeof token === 'function') {
        token = await token(config);
      }

      if (token && typeof token === 'object') {
        token = token.toString();
      }

      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
  }
}
